/**
 * grammars.js
 * Description: exports functions used for checking syntax of the program
 */

import {
  isBoolean,
  isBooleanLiteral,
  isItzToken,
  isRToken,
  isLiteralToken,
  isIdentifierToken,
  isExpressionToken,
  isUnaryOperatorToken,
  isBinaryBooleanOperatorToken,
  isInfOperatorToken,
  isAnToken,
  isArithmeticOperatorToken,
  acceptNextToken,
  checkNextToken,
  generateError,
} from "./helpers.js";

export function parseStatement(index, tokens, parentArray) {
  let currentToken = tokens[index];

  switch (currentToken.type) {
    case "VAR_DEC":
      parentArray.push(currentToken);
      index = parseVarDeclaration(index, tokens);
      break;

    case "IDENT":
      parentArray.push(currentToken);
      index = parseVarAssignment(index, tokens);
      break;

    case "VAR_IT":
      parentArray.push(currentToken);
      index = parseVarAssignment(index, tokens);
      break;

    case "INPUT":
      parentArray.push(currentToken);
      index = parseGimmeh(index, tokens);
      break;

    case "OUTPUT":
      parentArray.push(currentToken);
      index = parseVisible(index, tokens);
      break;

    case "SWITCH_START":
      parentArray.push(currentToken);
      index = parseSwitchBlock(index, tokens);
      break;

    case "SWITCH_BREAK":
      parentArray.push(currentToken);
      break;

    case "CODE_START":
      throw new Error(
        generateError(
          `HAI must only be placed at the beginning of the program.`
        )
      );

    case "CODE_END":
      throw new Error(
        generateError(`KTHXBYE must only be placed at the end of the program.`)
      );

    default:
      // Expression na walang variable na sumasalo
      if (currentToken.type.slice(0, 3) === "OP_") {
        index = parseExpression(index, tokens, parentArray);

        // Check if the next token after the unassigned expression is a prompt for If-Else Statement
        let lookAheadToken = tokens[index + 1];
        if (lookAheadToken && lookAheadToken.type === "IF_START") {
          //value of IT as the first element of O RLY? token

          parentArray.push(lookAheadToken);
          index = parseIfElseBlock(index, tokens);
        }
      }
  }

  return index;
}

// Parse whole switch block
export function parseSwitchBlock(index, tokens) {
  let wtfToken = tokens[index++];

  // Parse OMG (cases)
  for (let i = tokens[index]; i.type !== "SWITCH_DEF"; i = tokens[++index]) {
    index = parseSwitchCase(index, tokens, wtfToken);
  }

  // Parse OMGWTF (default case)
  index = parseSwitchCase(index, tokens, wtfToken);

  // Parse OIC
  let oicToken = checkNextToken(index, tokens, "IF_END");
  index = acceptNextToken(index, wtfToken, oicToken);

  return index;
}

// Parses one case of a switch statement
export function parseSwitchCase(index, tokens, wtfToken) {
  let omgToken = tokens[index];
  let litValueToken = tokens[index + 1];

  // If omgToken is OMG, parse switch case until next case or default case
  if (omgToken.type === "SWITCH_CASE") {
    wtfToken.value.push(omgToken);

    // OMG must be followed by literal value
    if (isLiteralToken(litValueToken)) {
      omgToken.value.push(litValueToken);
      return parseStatementsBlock(index + 1, tokens, litValueToken, [
        "SWITCH_CASE",
        "SWITCH_DEF",
      ]);
    } else {
      throw new Error(`Expected literal value.`, litValueToken);
    }
  }

  // Else if omgToken is default case, parse until OIC
  else if (omgToken.type === "SWITCH_DEF") {
    wtfToken.value.push(omgToken);
    return parseStatementsBlock(index + 1, tokens, omgToken, ["IF_END"]);
  } else throw new Error(generateError(`Expected OMG/OMGWTF.`, omgToken));
}

// <if> ::= <expr> O RLY? <statement> YA RLY <statement> NO WAI OIC
export function parseIfElseBlock(index, tokens) {
  let ifStartToken = tokens[++index];

  // Check YA RLY token then get statements
  let ifBlockToken = checkNextToken(index, tokens, "IF_TRUE");
  index = acceptNextToken(index, ifStartToken, ifBlockToken);
  index = parseStatementsBlock(index + 1, tokens, ifBlockToken, ["IF_FALSE"]);

  // Check NO WAI token then get statements
  let elseBlockToken = checkNextToken(index, tokens, "IF_FALSE");
  index = acceptNextToken(index, ifStartToken, elseBlockToken);
  index = parseStatementsBlock(index + 1, tokens, elseBlockToken, ["IF_END"]);

  // Check OIC token then get statements
  let oicToken = checkNextToken(index, tokens, "IF_END");
  index = acceptNextToken(index, ifStartToken, oicToken);

  return index;
}

// Parses the statements inside an if block while next keyword is not reached
export function parseStatementsBlock(
  index,
  tokens,
  parentToken,
  targetTokenTypes
) {
  let nextToken = tokens[index];

  while (nextToken && !targetTokenTypes.includes(nextToken.type)) {
    index = parseStatement(index, tokens, parentToken.value) + 1;
    nextToken = tokens[index];
  }

  if (!nextToken)
    throw new Error(
      generateError(`Expected ${targetTokenTypes}.`, tokens[index - 1])
    );

  return index - 1;
}

// <input> ::= GIMMEH varident
export function parseGimmeh(index, tokens) {
  let gimmehToken = tokens[index];
  let varidentToken = checkNextToken(index, tokens, "IDENT");
  index = acceptNextToken(index, gimmehToken, varidentToken);
  return index;
}

// <output> ::= VISIBLE [<expression> ...]
export function parseVisible(index, tokens) {
  let visibleToken = tokens[index];
  let nextToken = tokens[index + 1];

  //Error if nothing follows VISIBLE keyword
  if (!nextToken) throw new Error(generateError("Expected expression."));

  // While on the same line, getExpression
  while (nextToken && nextToken.line === visibleToken.line) {
    index = parseExpression(index + 1, tokens, visibleToken.value);
    nextToken = tokens[index + 1];
  }

  return index;
}

// <assignment> ::= <varident> R <expression>
export function parseVarAssignment(index, tokens) {
  let varidentToken = tokens[index];
  // Check R token then try to parse expression
  let RToken = tokens[index + 1];
  if (RToken && isRToken(RToken)) {
    ++index;

    let exprToken = tokens[index + 1];
    if (exprToken && isExpressionToken(exprToken))
      index = parseExpression(index + 1, tokens, varidentToken.value);
    else
      throw new Error(generateError(`Expected literal/identifier/expression.`));
  }
  return index;
}

// <declaration> ::= I HAS A <varident> | I HAS A <varident> ITZ <expression>
export function parseVarDeclaration(index, tokens) {
  let iHasAToken = tokens[index];

  // Check varident token
  let varToken = checkNextToken(index, tokens, "IDENT");
  index = acceptNextToken(index, iHasAToken, varToken);

  //
  let itzToken = tokens[index + 1];
  if (itzToken && isItzToken(itzToken)) {
    ++index;
    // Check expression token
    // Since an expression can be composed of multiple tokens, parse whole expression instead of checkNextToken
    let exprToken = tokens[index + 1];

    if (exprToken && isExpressionToken(exprToken))
      index = parseExpression(index + 1, tokens, iHasAToken.value);
    else
      throw new Error(
        generateError(`Expected literal/identifier/expression.`, exprToken)
      );
  }

  return index;
}

// EXPRESSIONS --------------------
// [1] Unary:  NOT
// [2] Binary Arithmetic (NUMBR/NUMBAR): ADD, SUB, MUL, DIV, MOD, MAX, MIN
// [3] Binary Boolean (TROOF): AND, OR, XOR, EQU, NEQ
// [4] Infinite Arguments (... AN ... AN ... AN ...): ALL, ANY Note: may MKAY dapat sa dulo
// <expression> ::= [SUM|DIFF|PRODUKT|QUOSHUNT|MOD|BIGGR|SMALLR|BOTH|EITHER|WON|ALL|ANY] OF <expr> AN <expr> |
//                  NOT [<varident> | <literal>] | <varident> | <literal>

// Helper function for expressions
export function parseExpression(index, tokens, parentArray) {
  if (isLiteralToken(tokens[index]) || isIdentifierToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return index;
  } else if (isUnaryOperatorToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return parseNotOperand(index, tokens, tokens[index].value);
  } else if (
    isBinaryBooleanOperatorToken(tokens[index]) ||
    isArithmeticOperatorToken(tokens[index])
  ) {
    parentArray.push(tokens[index]);
    return parseBinaryOperands(index, tokens, tokens[index].value);
  } else if (isInfOperatorToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return parseAnyAllOperands(index, tokens, tokens[index].value);
  }
}

export function parseBooleanExpression(index, tokens, parentArray) {
  if (isBooleanLiteral(tokens[index]) || isIdentifierToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return index;
  } else if (isUnaryOperatorToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return parseNotOperand(index, tokens, tokens[index].value);
  } else if (isBinaryBooleanOperatorToken(tokens[index])) {
    parentArray.push(tokens[index]);
    return parseBinaryOperands(index, tokens, tokens[index].value);
  }
}

// NOT [varident | literal | troof expression]
export function parseNotOperand(index, tokens, parentArray) {
  let next_token = tokens[index + 1];
  if (isBooleanLiteral(next_token) || isIdentifierToken(next_token)) {
    parentArray.push(next_token);
    return ++index;
  } else return parseExpression(index + 1, tokens, parentArray);
}

// BOTH, EITHER, WON, BOTH SAEM, DIFFRINT
export function parseBinaryOperands(index, tokens, parentArray) {
  let expr1_index = parseExpression(index + 1, tokens, parentArray);

  // 1st Expression accepted, check first for AN token
  let an_token = tokens[expr1_index + 1];
  if (!isAnToken(an_token))
    throw new Error(
      generateError("Expected AN. Got " + an_token.type, an_token)
    );

  // If AN token found
  expr1_index++;
  let expr2_index = parseExpression(expr1_index + 1, tokens, parentArray);
  return expr2_index;
}

// ALL, ANY
export function parseAnyAllOperands(index, tokens, parentArray) {
  while (
    tokens[index++] &&
    (isBoolean(tokens[index]) || isIdentifierToken(tokens[index]))
  ) {
    index = parseBooleanExpression(index, tokens, parentArray);

    if (tokens[index + 1].type === "INFOP_END") return index + 1;
    checkNextToken(index++, tokens, "MULOP_SEP");
  }

  throw new Error(
    generateError(`Expected boolean literal/variable.`, tokens[index])
  );
}
